import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader } from "@/components/Loader";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, FileText, Video, ClipboardList } from "lucide-react";

export default function ModulesPage() {
  const navigate = useNavigate();
  const [modules, setModules] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", youtube_url: "", estimated_minutes: 30, order: 0 });

  const load = () => api.get("/admin/modules").then((r) => setModules(r.data.modules)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title) return toast.error("Title is required");
    try {
      const r = await api.post("/admin/modules", { ...form, estimated_minutes: Number(form.estimated_minutes), order: Number(form.order), is_active: true });
      toast.success("Module created");
      setOpen(false);
      setForm({ title: "", description: "", youtube_url: "", estimated_minutes: 30, order: 0 });
      navigate(`/admin/modules/${r.data.id}`);
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!modules) return <Loader />;

  return (
    <div className="space-y-6" data-testid="admin-modules">
      <div className="flex items-center justify-between">
        <h1 className="text-[32px] font-bold text-[#1c1b1b]">Training Modules</h1>
        <button data-testid="new-module-btn" onClick={() => setOpen(true)}
          className="h-11 px-5 rounded-lg bg-[#003d9b] text-white font-semibold flex items-center gap-2 hover:bg-[#0052cc] transition-colors">
          <Plus className="w-4 h-4" /> New Module
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {modules.map((m) => (
          <button key={m.id} data-testid={`module-card-${m.id}`} onClick={() => navigate(`/admin/modules/${m.id}`)}
            className="text-left bg-white border border-[#E5E7EB] rounded-lg p-5 hover:border-[#003d9b] transition-colors">
            <div className="flex items-center justify-between">
              <p className="font-semibold text-[#1c1b1b]">{m.title}</p>
              {!m.is_active && <span className="text-xs text-[#9CA3AF]">Inactive</span>}
            </div>
            <p className="text-sm text-[#434654] mt-1 line-clamp-2">{m.description || "No description"}</p>
            <div className="flex gap-4 mt-4 text-xs text-[#434654]">
              <span className="flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> {m.slide_count} slides</span>
              <span className="flex items-center gap-1"><Video className="w-3.5 h-3.5" /> {m.youtube_url ? "Video" : "No video"}</span>
              <span className="flex items-center gap-1"><ClipboardList className="w-3.5 h-3.5" /> {m.assessment_sets?.length || 0} set(s)</span>
            </div>
          </button>
        ))}
        {modules.length === 0 && <p className="text-sm text-[#434654]" data-testid="modules-empty">No modules yet. Create your first module.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="new-module-dialog">
          <DialogHeader><DialogTitle>New Training Module</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <input data-testid="module-title-input" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="h-11 w-full rounded-lg border border-[#E5E7EB] px-3 focus:outline-none focus:ring-2 focus:ring-[#003d9b]" />
            <textarea data-testid="module-description-input" placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="min-h-[80px] w-full rounded-lg border border-[#E5E7EB] p-3 focus:outline-none focus:ring-2 focus:ring-[#003d9b]" />
            <input data-testid="module-youtube-input" placeholder="YouTube URL" value={form.youtube_url} onChange={(e) => setForm({ ...form, youtube_url: e.target.value })}
              className="h-11 w-full rounded-lg border border-[#E5E7EB] px-3 focus:outline-none focus:ring-2 focus:ring-[#003d9b]" />
            <div className="flex gap-3">
              <input data-testid="module-minutes-input" type="number" placeholder="Minutes" value={form.estimated_minutes} onChange={(e) => setForm({ ...form, estimated_minutes: e.target.value })}
                className="h-11 w-full rounded-lg border border-[#E5E7EB] px-3 focus:outline-none focus:ring-2 focus:ring-[#003d9b]" />
              <input data-testid="module-order-input" type="number" placeholder="Order" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })}
                className="h-11 w-full rounded-lg border border-[#E5E7EB] px-3 focus:outline-none focus:ring-2 focus:ring-[#003d9b]" />
            </div>
            <button data-testid="create-module-btn" onClick={create}
              className="w-full h-12 rounded-lg bg-[#003d9b] text-white font-semibold hover:bg-[#0052cc] transition-colors">Create & Edit</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
